<template>
  <div
    class="flex items-center h-6 bg-blue-700 text-white text-xs px-2 gap-3 shrink-0 select-none overflow-hidden"
    aria-label="Status bar"
  >

    <!-- LEFT: branch → error/warning count ──────────────────────────────────── -->

    <!-- Branch — click opens the git panel -->
    <button
      class="flex items-center gap-1 hover:bg-blue-600 px-1.5 py-0.5 rounded transition-colors shrink-0"
      :title="branchName !== '—' ? `Branch: ${branchName}` : 'No git status'"
      @click="$emit('openBranchSwitcher')"
    >
      <span aria-hidden="true">⎇</span>
      <span>{{ branchName }}</span>
    </button>

    <!-- Error / warning count from Monaco model markers.
         Monaco markers are not Vue-reactive: refreshMarkers() is called via
         monaco.editor.onDidChangeMarkers and when the active file changes. -->
    <button
      class="flex items-center gap-2 hover:bg-blue-600 px-1.5 py-0.5 rounded transition-colors shrink-0"
      title="Errors and warnings in the active file"
      @click="$emit('openBranchSwitcher')"
    >
      <span
        v-if="errorCount > 0"
        class="flex items-center gap-0.5 text-red-300"
        :title="`${errorCount} error${errorCount !== 1 ? 's' : ''}`"
      >
        <span aria-hidden="true">✕</span>
        <span>{{ errorCount }}</span>
      </span>
      <span
        v-if="warningCount > 0"
        class="flex items-center gap-0.5 text-yellow-300"
        :title="`${warningCount} warning${warningCount !== 1 ? 's' : ''}`"
      >
        <span aria-hidden="true">⚠</span>
        <span>{{ warningCount }}</span>
      </span>
      <span
        v-if="errorCount === 0 && warningCount === 0"
        class="text-blue-200"
        title="No errors or warnings"
      >✓</span>
    </button>

    <!-- Spacer — pushes right-side items to the far right -->
    <div class="flex-1" />

    <!-- RIGHT: language → encoding → LESS → active file → repo ─────────────── -->

    <!-- Language mode of the active file -->
    <span
      v-if="editorStore.activeFile"
      class="text-blue-200 shrink-0 capitalize"
    >{{ editorStore.activeFile.language }}</span>

    <!-- File encoding (always UTF-8; the server reads/writes UTF-8 only) -->
    <span class="text-blue-200 shrink-0">UTF-8</span>

    <!-- LESS compiler status — driven by WebSocket less:compiled / less:error events -->
    <span
      class="shrink-0 transition-colors"
      :class="lessStatusClass"
      :title="lessStatusTitle"
    >{{ lessStatusLabel }}</span>

    <!-- Active filename — truncated; full path in tooltip -->
    <span
      v-if="editorStore.activeFile"
      class="text-blue-200 truncate max-w-48 shrink-0"
      :title="editorStore.activeFile.path"
    >{{ activeFileName }}</span>

    <!-- Active repository -->
    <span
      class="text-blue-200 truncate max-w-32 shrink-0"
      :title="repoStore.activeRepo?.name"
    >{{ repoStore.activeRepo?.name ?? 'No repo' }}</span>

    <!-- Logged-in user — far right -->
    <span
      v-if="repoStore.currentUser"
      class="text-blue-100 shrink-0 font-medium"
      :title="`Eingeloggt als ${repoStore.currentUser}`"
    >{{ repoStore.currentUser }}</span>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import * as monaco        from 'monaco-editor'
import { useGitStore }    from '@/stores/gitStore'
import { useRepoStore }   from '@/stores/repoStore'
import { useEditorStore } from '@/stores/editorStore'
import { onLessCompiled, onLessError } from '@/websocket/wsClient'

defineEmits<{ (e: 'openBranchSwitcher'): void }>()

const gitStore    = useGitStore()
const repoStore   = useRepoStore()
const editorStore = useEditorStore()

// ── Branch ────────────────────────────────────────────────────────────────────

const branchName = computed(() => gitStore.status?.current ?? '—')

// ── Active file ───────────────────────────────────────────────────────────────

const activeFileName = computed(() =>
  editorStore.activeFile?.path.split('/').pop() ?? ''
)

// ── Error / warning count ─────────────────────────────────────────────────────
// Monaco markers are not Vue-reactive — we subscribe to Monaco's own change
// event and also watch activeFile so counts update on tab switches.

const errorCount   = ref(0)
const warningCount = ref(0)

function refreshMarkers(): void {
  const file = editorStore.activeFile
  if (!file) {
    errorCount.value = 0
    warningCount.value = 0
    return
  }
  const model = editorStore.getModel(file.path)
  if (!model) {
    errorCount.value = 0
    warningCount.value = 0
    return
  }
  const markers  = monaco.editor.getModelMarkers({ resource: model.uri })
  errorCount.value   = markers.filter(m => m.severity === monaco.MarkerSeverity.Error).length
  warningCount.value = markers.filter(m => m.severity === monaco.MarkerSeverity.Warning).length
}

// Refresh whenever the active tab changes (different Monaco model, different marker set).
watch(() => editorStore.activeFile, refreshMarkers)

// ── LESS compiler status ──────────────────────────────────────────────────────

type LessStatus = 'idle' | 'compiling' | 'compiled' | 'error'
const lessStatus = ref<LessStatus>('idle')

const lessStatusLabel = computed((): string => {
  switch (lessStatus.value) {
    case 'compiling': return 'LESS: compiling…'
    case 'compiled':  return 'LESS: ✓'
    case 'error':     return 'LESS: ✗'
    default:          return 'LESS: idle'
  }
})

const lessStatusTitle = computed((): string => {
  switch (lessStatus.value) {
    case 'compiling': return 'LESS compilation in progress'
    case 'compiled':  return 'LESS compiled successfully'
    case 'error':     return 'LESS compilation error — check the file'
    default:          return 'LESS compiler idle'
  }
})

const lessStatusClass = computed(() => ({
  'text-green-300':  lessStatus.value === 'compiled',
  'text-red-300':    lessStatus.value === 'error',
  'text-yellow-300': lessStatus.value === 'compiling',
  'text-blue-200':   lessStatus.value === 'idle',
}))

// ── Lifecycle ─────────────────────────────────────────────────────────────────

let markerDisposable: monaco.IDisposable | null = null
let unsubCompiled:    (() => void) | null = null
let unsubError:       (() => void) | null = null

onMounted(() => {
  // Monaco fires onDidChangeMarkers for any model — re-read the active file's
  // count each time. This covers LSP-style diagnostic updates on the open file.
  markerDisposable = monaco.editor.onDidChangeMarkers(() => refreshMarkers())

  unsubCompiled = onLessCompiled(() => {
    lessStatus.value = 'compiled'
    // Reset to idle after 4 s so the green indicator doesn't linger forever.
    setTimeout(() => {
      if (lessStatus.value === 'compiled') lessStatus.value = 'idle'
    }, 4_000)
  })

  unsubError = onLessError(() => {
    lessStatus.value = 'error'
  })

  // Seed the initial count in case a file is already open at mount time.
  refreshMarkers()
})

onUnmounted(() => {
  markerDisposable?.dispose()
  unsubCompiled?.()
  unsubError?.()
})
</script>
